import type { ThemeKey, DefaultThemeStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React, { Children, useEffect, useRef } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { throttle } from 'lodash';
import { forceCheck } from 'react-lazyload';
import { FocusOn, AutoFocusInside } from 'react-focus-on';
import { OVER_BOT } from 'Components/frameworks/portal/Portal.constants';
import { ICONS } from 'Components/utils/icon/icons';
import { BodyLocker } from 'Components/frameworks/body-locker/BodyLocker';
import { Icon } from 'Components/frameworks/icon/Icon';
import Portal from 'Components/frameworks/portal/Portal';
import { themes, POSITIONS_CLASSES } from './SidePanel.themes';
type Props = {
  children?: ReactNode;
  className?: string;
  closeLabel?: string;
  isExpanded?: boolean;
  onClose?: (arg0: React.SyntheticEvent<HTMLElement>) => void;
  portalName?: string;
  position?: 'top' | 'right' | 'bottom' | 'left';
  theme?: ThemeKey<typeof themes>;
};
export const SidePanel = ({
  children,
  className,
  closeLabel,
  isExpanded,
  onClose,
  portalName = OVER_BOT,
  position = 'bottom',
  theme = 'default',
  ...rest
}: Props) => {
  const sidePanelBodyref: {
    current: HTMLElement | null | undefined;
  } = useRef();
  const onScroll = throttle(() => {
    forceCheck();
  }, 250);
  useEffect(() => {
    sidePanelBodyref &&
      sidePanelBodyref.current &&
      sidePanelBodyref.current.addEventListener('scroll', onScroll);
    return () => {
      sidePanelBodyref &&
        sidePanelBodyref.current &&
        sidePanelBodyref.current.removeEventListener('scroll', onScroll);
    };
  }, []);
  useEffect(() => {
    if (isExpanded) {
      forceCheck();
    }
  }, [isExpanded]);

  if (!Children.count(children)) {
    return null;
  }

  const { thCloseButton, thCloseButtonLabel, thRoot } = themes[theme] as DefaultThemeStyles<
    typeof themes
  >;
  const positionClasses = POSITIONS_CLASSES[position];
  return (
    <Portal name={portalName}>
      <BodyLocker isLocked={isExpanded}>
        {isExpanded && (
          <div
            className="animation-duration-500 fixed inset-0 h-screen animate-fadeIn bg-black opacity-80"
            data-testid="SidePanel-backdrop"
          />
        )}
        <div {...rest} aria-hidden={!isExpanded} role="dialog">
          <div className="isolate" role="document">
            <FocusOn
              allowPinchZoom
              enabled={isExpanded}
              noIsolation
              onClickOutside={onClose as unknown as (event: MouseEvent | TouchEvent) => void}
              onEscapeKey={onClose as unknown as (event: Event) => void}
            >
              {isExpanded && (
                <AutoFocusInside>
                  <button
                    className={classnames('fixed z-2', thCloseButton, positionClasses.button)}
                    onClick={onClose}
                    type="button"
                  >
                    {closeLabel && <span className={thCloseButtonLabel}>{closeLabel}</span>}
                    <Icon name={ICONS.CROSS} width="1em" />
                  </button>
                </AutoFocusInside>
              )}

              <aside
                className={classnames(
                  thRoot,
                  positionClasses.pin,
                  positionClasses.style,
                  'fixed z-1 h-full w-full transform overflow-y-scroll transition-all duration-250 ease-in',
                  {
                    [positionClasses.transform]: !isExpanded,
                    'translate-x-0 translate-y-0': isExpanded,
                  },
                  className,
                )}
                ref={sidePanelBodyref}
              >
                {isExpanded && children}
              </aside>
            </FocusOn>
          </div>
        </div>
      </BodyLocker>
    </Portal>
  );
};
